import { get_token, get_user_details } from "@/router";
import Swal from "sweetalert2";

export default {
  props: {
    event_id: {
      type: [Number, String],
      default: null,
    },
    event: {
      type: Object,
      default: null,
    },
    inline: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return ({
      current_event_id: null,
      documents: [],
      selected_files: [],
      is_loading: false,
      is_uploading: false,
      is_dragging: false,
      preview_url: null,
      preview_type: null,
      can_upload_documents: false,
      user_details: null,
      file_error: "",
      public_slides: true,
      is_saving_visibility: false,
      accepted_types:
        ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt",
    });
  },
  watch: {
    event: {
      immediate: true,
      deep: false,
      handler(next) {
        if (!this.inline) return;
        if (!next || typeof next !== "object") return;
        this.init_inline(next);
      },
    },
    event_id: {
      immediate: true,
      handler(next) {
        if (!this.inline) return;
        if (!next) return;
        this.current_event_id = next;
        this.fetch_documents();
      },
    },
  },
  methods: {
    init_inline(event) {
      this.user_details = get_user_details();
      this.current_event_id = event?.id ?? this.event_id;

      const role = String(this.user_details?.user_role || "").toLowerCase();
      const is_admin = role === "admin";
      const event_owner_id = event?.user?.id;
      const is_owner =
        String(event_owner_id) === String(this.user_details?.user_id);

      this.can_upload_documents = is_admin || is_owner;
      this.public_slides = event?.public_slides !== false;
      this.reset();

      if (Array.isArray(event?.event_files)) {
        this.documents = this.filter_documents(event.event_files);
      }

      this.fetch_documents();
    },
    open_document_modal(event = null) {
      this.user_details = get_user_details();
      this.current_event_id = event?.id ?? this.event_id;

      const role = String(this.user_details?.user_role || "").toLowerCase();
      const is_admin = role === "admin";
      const event_owner_id = event?.user?.id;
      const is_owner =
        String(event_owner_id) === String(this.user_details?.user_id);

      this.can_upload_documents = is_admin || is_owner;
      this.public_slides = event?.public_slides !== false;
      this.reset();

      if (Array.isArray(event?.event_files)) {
        this.documents = this.filter_documents(event.event_files);
      }

      this.fetch_documents();
      if (!this.inline) {
        $("#event_document_modal").modal("show");
      }
    },
    reset() {
      this.selected_files.forEach((item) => {
        if (item?.preview_url) URL.revokeObjectURL(item.preview_url);
      });
      this.selected_files = [];
      this.is_uploading = false;
      this.is_dragging = false;
      this.preview_url = null;
      this.preview_type = null;
      this.file_error = "";
    },
    filter_documents(files) {
      return (files || []).filter((file) => {
        const typeName = String(file?.event_file_type?.name || "").toLowerCase();
        if (!typeName) return true;
        if (typeName.includes("gallery")) return false;
        if (typeName.includes("agenda")) return false;
        return typeName.includes("document") || typeName.includes("resource");
      });
    },
    getToken() {
      return get_token();
    },
    trigger_file_picker() {
      this.$refs.documentFilesInput?.click();
    },
    on_files_selected(e) {
      const files = Array.from(e?.target?.files || []);
      this.add_selected_files(files);
      if (e?.target) e.target.value = "";
    },
    on_drag_over() {
      this.is_dragging = true;
    },
    on_drag_leave() {
      this.is_dragging = false;
    },
    on_drop(e) {
      this.is_dragging = false;
      const files = Array.from(e?.dataTransfer?.files || []);
      this.add_selected_files(files);
    },
    file_key(file) {
      return `${file?.name || "file"}-${file?.size || 0}-${file?.lastModified || 0}`;
    },
    is_allowed_file(file) {
      const name = String(file?.name || "").toLowerCase();
      const allowed = [
        ".pdf",
        ".doc",
        ".docx",
        ".xls",
        ".xlsx",
        ".ppt",
        ".pptx",
        ".txt",
      ];
      return allowed.some((ext) => name.endsWith(ext));
    },
    add_selected_files(files) {
      const incoming = (files || []).filter((f) => this.is_allowed_file(f));
      if (!incoming.length) {
        this.file_error = "Only PDF, Office, or image files are allowed.";
        return;
      }
      this.file_error = "";

      const existingKeys = new Set(this.selected_files.map((i) => i.key));
      const next = [...this.selected_files];

      incoming.forEach((file) => {
        const key = this.file_key(file);
        if (existingKeys.has(key)) return;
        existingKeys.add(key);
        next.push({
          key,
          file,
          name: file.name,
          preview_url: URL.createObjectURL(file),
        });
      });

      this.selected_files = next;
    },
    remove_selected_file(index) {
      const item = this.selected_files.at(index);
      if (item?.preview_url) URL.revokeObjectURL(item.preview_url);
      this.selected_files.splice(index, 1);
    },
    clear_selected_files() {
      this.selected_files.forEach((item) => {
        if (item?.preview_url) URL.revokeObjectURL(item.preview_url);
      });
      this.selected_files = [];
    },
    fetch_documents() {
      const event_id = this.current_event_id ?? this.event_id;
      if (!event_id) return;

      this.is_loading = true;
      $.ajax({
        url: `${process.env.VUE_APP_BACKEND_API_URL}/event_management/get_event_resources/`,
        type: "POST",
        headers: {
          Authorization: `Token ${this.getToken()}`,
          "Content-Type": "application/json",
        },
        data: JSON.stringify({ event_id }),
        success: (response) => {
          if (response.status !== "success") return;
          this.documents = response.data || [];
        },
        complete: () => {
          this.is_loading = false;
        },
        error: () => {
          this.is_loading = false;
        },
      });
    },
    save_slides_visibility() {
      const event_id = this.current_event_id ?? this.event_id;
      if (!event_id) return;

      const next_value = !!this.public_slides;
      this.is_saving_visibility = true;

      const formData = new FormData();
      formData.append("event_id", event_id);
      formData.append("public_slides", next_value ? "true" : "false");

      $.ajax({
        url: `${process.env.VUE_APP_BACKEND_API_URL}/event_management/toggle_event_media_visibility/`,
        type: "POST",
        headers: {
          Authorization: `Token ${this.getToken()}`,
        },
        data: formData,
        processData: false,
        contentType: false,
        success: (response) => {
          if (response.status === "success") {
            this.$emit("visibility-updated", next_value);
            Swal.fire({
              icon: "success",
              title: "Saved",
              text: "Materials visibility updated.",
              showConfirmButton: false,
              timer: 1500,
              position: "bottom-end",
              toast: true
            });
          }
        },
        error: (xhr) => {
          this.public_slides = !next_value;
          Swal.fire({
            icon: "error",
            title: "Update failed",
            text: xhr?.responseJSON?.message || "Failed to update materials visibility.",
            showConfirmButton: false,
            timer: 3000,
            position: "bottom-end",
            toast: true
          });
        },
        complete: () => {
          this.is_saving_visibility = false;
        },
      });
    },
    upload_documents() {
      if (!this.can_upload_documents) {
        Swal.fire({
          icon: "error",
          title: "Not allowed",
          text: "Only the event creator or an admin can upload documents.",
          showConfirmButton: false,
          timer: 2500,
          position: 'bottom-end',
          toast: true
        });
        return;
      }
      const event_id = this.current_event_id ?? this.event_id;
      if (!event_id) {
        Swal.fire({
          icon: "error",
          title: "Missing event",
          text: "Please select an event before uploading documents.",
          timer: 2500,
          showConfirmButton: false,
          position: 'bottom-end',
          toast: true
        });
        return;
      }
      if (!this.selected_files.length) return;

      const formData = new FormData();
      formData.append("event_id", event_id);
      formData.append("event_file_type", "document");
      formData.append("public_slides", this.public_slides ? "true" : "false");
      this.selected_files.forEach((item) => {
        formData.append("files", item.file);
      });

      this.is_uploading = true;
      $.ajax({
        url: `${process.env.VUE_APP_BACKEND_API_URL}/event_management/upload_event_resources/`,
        type: "POST",
        headers: {
          Authorization: `Token ${this.getToken()}`,
        },
        data: formData,
        processData: false,
        contentType: false,
        success: (response) => {
          if (response.status === "success") {
            Swal.fire({
              icon: "success",
              title: "Uploaded",
              text: response.message || "Documents uploaded successfully.",
              showConfirmButton: false,
              timer: 1500,
              position: 'bottom-end',
              toast: true
            }).then(() => {
              this.clear_selected_files();
              this.documents.unshift(...response.data);
              this.$emit("documents-updated", response.data);
              this.$emit("visibility-updated", this.public_slides);
            });
          }
        },
        error: (xhr) => {
          Swal.fire({
            icon: "error",
            title: "Upload failed",
            text: xhr?.responseJSON?.message || "Failed to upload documents.",
            showConfirmButton: false,
            timer: 3000,
            position: 'bottom-end',
            toast: true
          });
        },
        complete: () => {
          this.is_uploading = false;
        },
      });
    },
    delete_document(item) {
      if (!this.can_upload_documents) return;
      const event_file_id = item?.id;
      if (!event_file_id) return;

      Swal.fire({
        title: "Delete document?",
        text: "This action cannot be undone.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete",
        position: 'bottom-end',
        toast: true
      }).then((result) => {
        if (!result.isConfirmed) return;

        $.ajax({
          url: `${process.env.VUE_APP_BACKEND_API_URL}/event_management/delete_event_media/`,
          type: "POST",
          headers: {
            Authorization: `Token ${this.getToken()}`,
            "Content-Type": "application/json",
          },
          data: JSON.stringify({ event_file_id }),
          success: (response) => {
            if (response.status === "success") {
              this.documents = this.documents.filter(
                (doc) => String(doc.id) !== String(event_file_id)
              );
              this.$emit("document-deleted", event_file_id);
              Swal.fire({
                icon: "success",
                title: "Deleted",
                showConfirmButton: false,
                timer: 1200,
                position: 'bottom-end',
                toast: true
              });
            }
          },
          error: (xhr) => {
            Swal.fire({
              icon: "error",
              title: "Delete failed",
              text: xhr?.responseJSON?.message || "Failed to delete document.",
              showConfirmButton: false,
              timer: 3000,
              position: 'bottom-end',
              toast: true
            });
          },
        });
      });
    },
    get_file_url(item) {
      return (
        item?.file?.file_url ||
        item?.file?.s3_url ||
        item?.file_url ||
        item?.url ||
        ""
      );
    },
    get_file_name(item) {
      const url = this.get_file_url(item);
      if (!url) return "Document";
      try {
        const parsed = new URL(url);
        const filename = parsed.pathname.split("/").pop() || "";
        return decodeURIComponent(filename);
      } catch {
        const parts = url.split("/");
        return decodeURIComponent(parts.at(parts.length - 1) || "Document");
      }
    },
    get_file_type(url) {
      if (typeof url !== "string") return "unknown";
      try {
        const parsed_url = new URL(url);
        const filename = parsed_url.pathname.split("/").pop();
        const extension = String(filename || "").split(".").pop().toLowerCase();

        if (extension === "pdf") return "pdf";
        if (extension === "txt") return "txt";
        if (["doc", "docx"].includes(extension)) return "word";
        if (["xls", "xlsx"].includes(extension)) return "excel";
        if (["ppt", "pptx"].includes(extension)) return "powerpoint";

        return "unknown";
      } catch {
        return "unknown";
      }
    },
    get_file_type_label(item) {
      const type = this.get_file_type(this.get_file_url(item));
      if (type === "word") return "Word document";
      if (type === "excel") return "Excel spreadsheet";
      if (type === "powerpoint") return "PowerPoint presentation";
      if (type === "pdf") return "PDF document";
      if (type === "txt") return "TXT document";
      return "Document";
    },
    get_icon_class(item) {
      const type = this.get_file_type(this.get_file_url(item)).toLowerCase();;
      if (type.includes('pdf'))
        return 'https://cdn-icons-png.flaticon.com/128/337/337946.png'; // PDF icon
      if (type.includes('doc') || type.includes('docx')  || type.includes('word'))
        return 'https://cdn-icons-png.flaticon.com/128/337/337932.png'; // Word icon
      if (type.includes('xls') || type.includes('xlsx')  || type.includes('excel'))
        return 'https://cdn-icons-png.flaticon.com/128/337/337958.png'; // Excel icon
      if (type.includes('ppt') || type.includes('pptx') || type.includes('powerpoint'))
        return 'https://cdn-icons-png.flaticon.com/128/337/337950.png'; // PowerPoint icon

      return 'https://cdn-icons-png.flaticon.com/128/337/337940.png'; // Default file icon
    },
    open_preview(item) {
      const file_url = this.get_file_url(item);
      if (!file_url) return;
      const file_type = this.get_file_type(file_url);

      if (
        file_type === "word" ||
        file_type === "excel" ||
        file_type === "powerpoint"
      ) {
        this.preview_url =
          "https://view.officeapps.live.com/op/embed.aspx?src=" +
          encodeURIComponent(file_url);
      } else {
        this.preview_url = file_url;
      }
      this.preview_type = file_type;
      $("#event_document_preview_modal").modal("show");
    },
    close_preview() {
      this.preview_url = null;
      this.preview_type = null;
      $("#event_document_preview_modal").modal("hide");
    },
    download_document(item) {
      const url = this.get_file_url(item);
      if (!url) return;
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener";
      link.click();
    },
  },
};
